import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Clock, Bell, Home, FileText, Heart, Check } from 'lucide-react';

const STEPS = [
  { icon: Mail, label: 'Order Confirmed' },
  { icon: Clock, label: "We'll Process It Soon" },
  { icon: Bell, label: "You'll Get an Update" },
];

const CheckoutCompleted = () => (
  <main
    className="relative bg-[#fef7ec] overflow-hidden"
    style={{ paddingTop: 88 }}
  >
    {/*
      Height comes from padding, not min-h-screen - the decorative bits below
      are positioned relative to this wrapper, so they stay pinned close to
      the card on every screen instead of drifting to the real viewport
      bottom on tall/short screens. paddingTop above matches the site-wide
      88px offset used to clear the fixed header (see BrowseRestaurants).
    */}
    <div className="relative max-w-5xl mx-auto px-4 pt-8 sm:pt-10 pb-14 sm:pb-16">
      <div className="absolute -top-16 -left-16 sm:-top-20 sm:-left-20 w-56 h-56 sm:w-72 sm:h-72 bg-pink-100/70 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -top-14 -right-16 sm:-top-16 sm:-right-20 w-56 h-56 sm:w-72 sm:h-72 bg-pink-100/70 rounded-full blur-2xl pointer-events-none" />

      <div
        className="hidden lg:block absolute top-6 left-0 text-gray-400/80 pointer-events-none"
        style={{ fontFamily: "'Caveat', cursive" }}
      >
        <p className="text-3xl leading-8 -rotate-3">
          Good
          <br />
          Food
          <br />
          Happier
          <br />
          <span className="underline decoration-gray-300 decoration-2 underline-offset-4">
            People
          </span>
        </p>
        <Heart className="w-5 h-5 mt-2 ml-1 text-gray-300" />
        <svg
          width="140"
          height="90"
          viewBox="0 0 140 90"
          className="mt-2 -ml-2 text-gray-300"
          fill="none"
        >
          <path
            d="M2 2C10 40 40 70 70 74C100 78 120 60 138 30"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="4 5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div
        className="hidden lg:block absolute top-6 right-0 text-gray-400/80 text-right pointer-events-none"
        style={{ fontFamily: "'Caveat', cursive" }}
      >
        <p className="text-3xl leading-8 rotate-3">
          Meals
          <br />
          That Move
          <br />
          <span className="underline decoration-gray-300 decoration-2 underline-offset-4">
            You
          </span>{' '}
          <Heart className="w-4 h-4 inline text-gray-300 -mt-1" />
        </p>
        <svg
          width="150"
          height="90"
          viewBox="0 0 150 90"
          className="mt-2 -mr-2 ml-auto text-gray-300"
          fill="none"
        >
          <path
            d="M148 2C138 40 108 70 78 74C48 78 28 60 2 34"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="4 5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 px-6 sm:px-10 py-8 sm:py-10 text-center">
        <div className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto">
          <div className="absolute inset-0 rounded-full bg-panda-tint animate-pulse" />
          <img
            src="/panda_hero_scooter.jpg"
            alt="Panda On Wheels"
            className="relative w-full h-full object-cover rounded-full ring-4 ring-white shadow-md"
            referrerPolicy="no-referrer"
          />
          <div className="absolute -bottom-1 -right-1 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#65A30D] ring-4 ring-white flex items-center justify-center shadow-sm">
            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} />
          </div>
        </div>

        <h1 className="mt-5 text-2xl sm:text-3xl md:text-4xl font-heading font-extrabold text-[#202124]">
          Thank you!
        </h1>
        <p className="mt-1 text-base sm:text-lg md:text-xl font-heading font-bold text-[#202124]">
          Your order has been submitted
        </p>
        <p className="mt-2 text-sm text-gray-500">
          You will receive a response shortly!
        </p>

        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-8">
          {STEPS.map(step => (
            <div key={step.label} className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-panda-tint flex items-center justify-center">
                <step.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#E53935]" />
              </div>
              <span className="text-[10px] sm:text-xs font-semibold text-gray-600 leading-snug">
                {step.label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-sm shadow-md shadow-red-500/20 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go to Home
          </Link>
          <Link
            to="/my-orders"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-[#E53935] text-[#E53935] hover:bg-red-50 font-heading font-bold text-sm transition-colors"
          >
            <FileText className="w-4 h-4" />
            View My Orders
          </Link>
        </div>

        <div className="flex items-center gap-3 mt-9">
          <div className="flex-1 h-px bg-gray-200" />
          <Heart className="w-4 h-4 text-[#E53935] fill-[#E53935]" />
          <div className="flex-1 h-px bg-gray-200" />
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Thank you for choosing Panda On Wheels!
        </p>
      </div>
    </div>
  </main>
);

export default CheckoutCompleted;
